import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import InfiniteScroll from 'react-infinite-scroller';
import Errors from 'components/shared/Errors';
import Main from 'components/templates/Main';
import Success from 'components/shared/Success';
import { userId as userIdSelector } from 'selectors/user';
import Api from 'api';
import { showSnackbar } from 'actions';
import ReviewsList from '../components/ReviewsList';
import {
  fetchReviewsRequest,
  postReviewReplyRequest,
  resetList,
  setReplyFormId,
  deleteReviewReplyRequest,
  deleteReviewRequest,
} from '../redux/actionCreators';
import * as selectors from '../redux/selectors';

const ReviewsListContainer = () => {
  const dispatch = useDispatch();
  const [editingReply, setEditing] = React.useState(null);
  const error = useSelector(selectors.error);
  const isLoading = useSelector(selectors.isLoading);
  const isUpdating = useSelector(selectors.isUpdating);
  const list = useSelector(selectors.list);
  const nextPage = useSelector(selectors.nextPage);
  const replyFormId = useSelector(selectors.replyFormId);
  const success = useSelector(selectors.success);
  const userId = useSelector(userIdSelector);

  const fetchMore = () => {
    if (!isLoading) {
      dispatch(fetchReviewsRequest({ nextPage }));
    }
  };

  React.useEffect(() => {
    if (!isLoading) {
      dispatch(resetList());

      fetchMore();
    }

    return () => {
      dispatch(resetList());
    };
  }, []);

  const sendReply = (data) => {
    dispatch(postReviewReplyRequest({ data, reviewId: replyFormId }));
  };

  const deleteReview = async (id) => {
    dispatch(deleteReviewRequest({ id }));
  };

  const deleteReviewReply = async (id) => {
    dispatch(deleteReviewReplyRequest({ id }));
  };

  const setEditingReply = (replyId) => {
    setEditing(replyId);
  };

  const setReplyFormIdFn = (value) => {
    dispatch(setReplyFormId(value));
  };

  const editReply = async (body) => {
    try {
      await Api.updateReviewReply(editingReply, body);

      dispatch(showSnackbar({
        severity: 'success',
        message: {
          id: 'reply.editSuccess',
          defaultMessage: 'The reply has been modified',
        },
      }));
    } catch (err) {
      dispatch(showSnackbar({
        severity: 'error',
        message: {
          id: 'reply.editError',
          defaultMessage: 'The reply has been modified',
        },
      }));
    }

    setEditing(null);
    dispatch(resetList());

    dispatch(fetchReviewsRequest({ nextPage: null }));
  };

  return (
    <Main title={(
      <FormattedMessage
        id="Reviews"
        defaultMessage="Reviews"
      />
    )}
    >
      <InfiniteScroll
        initialLoad={false}
        loadMore={fetchMore}
        hasMore={!!nextPage && !isLoading && !isUpdating}
        threshold={10}
      >
        <ReviewsList
          {...{
            isUpdating,
            isLoading,
            list,
            replyFormId,
            userId,
            sendReply,
            setReplyFormId: setReplyFormIdFn,
            deleteReview,
            deleteReviewReply,
            editReply,
            editingReply,
            setEditingReply,
          }}
        />
      </InfiniteScroll>
      <Success success={success} />
      <Errors errors={error} />
    </Main>
  );
};

export default ReviewsListContainer;
